import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Plus,
  Star,
  Building,
  X,
  Award,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import Badge from '@/components/Badge';
import AnimatedPressable from '@/components/AnimatedPressable';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';

export default function SerialFounderScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();

  const startupProfile = useProfileStore((s) => s.startupProfile);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);

  const [ventures, setVentures] = useState<string[]>(
    startupProfile?.previousVentures ?? []
  );
  const [newVenture, setNewVenture] = useState('');

  const handleAddVenture = () => {
    if (newVenture.trim() && ventures.length < 10) {
      setVentures([...ventures, newVenture.trim()]);
      setNewVenture('');
    }
  };

  const handleRemoveVenture = (index: number) => {
    setVentures(ventures.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (startupProfile) {
      setStartupProfile({
        ...startupProfile,
        isSerialFounder: ventures.length > 0,
        previousVentures: ventures.length > 0 ? ventures : undefined,
      });
    }
    router.back();
  };

  const isSerialFounder = ventures.length > 0;

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(500)}
          className="px-5 pt-4 pb-4"
        >
          <View className="flex-row items-center justify-between">
            <Pressable
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
            <AnimatedPressable
              onPress={handleSave}
              className="px-4 py-2 rounded-full"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-sm">
                Save
              </Text>
            </AnimatedPressable>
          </View>
        </Animated.View>

        {/* Title */}
        <Animated.View
          entering={FadeInDown.delay(200).duration(500)}
          className="px-5 pb-6"
        >
          <View className="flex-row items-center gap-3 mb-2">
            <Star size={24} color={colors.amber.main} />
            <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
              Founder Credibility
            </Text>
          </View>
          <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
            Serial Founder Badge
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
            Have you founded other companies?
          </Text>
        </Animated.View>

        <KeyboardAwareBody
          className="flex-1 px-5"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Animated.View entering={FadeInUp.delay(300).duration(500)} className="gap-6 pb-8">
            {/* Badge Preview */}
            <View
              className="p-5 rounded-2xl items-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <View
                className="w-16 h-16 rounded-full items-center justify-center mb-4"
                style={{
                  backgroundColor: isSerialFounder
                    ? colors.amber.light
                    : colors.border.light,
                }}
              >
                <Award
                  size={32}
                  color={isSerialFounder ? colors.amber.main : colors.text.tertiary}
                />
              </View>

              {isSerialFounder ? (
                <>
                  <Badge type="serial-founder" size="lg" />
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm text-center mt-3"
                  >
                    You've founded {ventures.length} other{' '}
                    {ventures.length === 1 ? 'company' : 'companies'}
                  </Text>
                </>
              ) : (
                <>
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-lg font-semibold text-center"
                  >
                    Not a Serial Founder Yet
                  </Text>
                  <Text
                    style={{ color: colors.text.tertiary }}
                    className="text-sm text-center mt-2"
                  >
                    Add previous ventures to earn the badge
                  </Text>
                </>
              )}
            </View>

            {/* Info */}
            <View
              className="p-4 rounded-xl"
              style={{ backgroundColor: colors.amber.light }}
            >
              <Text style={{ color: colors.amber.main }} className="text-sm leading-relaxed">
                Serial founders have a{' '}
                <Text className="font-semibold">higher success rate</Text> and are more
                attractive to investors. Adding previous ventures boosts your credibility.
              </Text>
            </View>

            {/* Ventures List */}
            <View>
              <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mb-3">
                Previous Ventures
              </Text>

              {ventures.length > 0 && (
                <View className="gap-2 mb-4">
                  {ventures.map((venture, index) => (
                    <View
                      key={index}
                      className="flex-row items-center p-3 rounded-xl"
                      style={{ backgroundColor: colors.surface.primary }}
                    >
                      <View
                        className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        <Building size={20} color={colors.primary[600]} />
                      </View>
                      <Text
                        style={{ color: colors.text.primary }}
                        className="flex-1 font-medium"
                      >
                        {venture}
                      </Text>
                      <Pressable
                        onPress={() => handleRemoveVenture(index)}
                        className="p-2"
                      >
                        <X size={18} color={colors.text.tertiary} />
                      </Pressable>
                    </View>
                  ))}
                </View>
              )}

              {/* Add Venture Input */}
              {ventures.length < 10 && (
                <View className="flex-row gap-2">
                  <TextInput
                    style={{
                      flex: 1,
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                      borderRadius: 12,
                      padding: 16,
                      color: colors.text.primary,
                      fontSize: 16,
                    }}
                    placeholder="Company name"
                    placeholderTextColor={colors.text.tertiary}
                    value={newVenture}
                    onChangeText={setNewVenture}
                    onSubmitEditing={handleAddVenture}
                    returnKeyType="done"
                  />
                  <AnimatedPressable
                    onPress={handleAddVenture}
                    disabled={!newVenture.trim()}
                    className="w-14 h-14 rounded-xl items-center justify-center"
                    style={{
                      backgroundColor: newVenture.trim()
                        ? colors.primary[600]
                        : colors.border.medium,
                    }}
                  >
                    <Plus size={24} color={colors.text.inverse} />
                  </AnimatedPressable>
                </View>
              )}

              <Text style={{ color: colors.text.tertiary }} className="text-xs mt-3">
                Include companies you've founded or co-founded, even if they're no longer active
              </Text>
            </View>
          </Animated.View>
        </KeyboardAwareBody>
      </SafeAreaView>
    </View>
  );
}
